import { useContext, useEffect, useRef, useState } from 'react';
import { Box, Chip, Grid, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { Description, DoneAll, Print } from '@mui/icons-material';
import { ArmazenagemContext } from 'contexts/ArmazenagemContext';
import { toastWarning } from 'utils/toast';
import { decryptURL } from 'utils/functions';
import { useDialog } from 'components/Modals';
import EtiquetaModal from 'components/Modals/EtiquetaModal';
import Container from 'components/Container';
import Button from 'components/Button';
import Header from 'components/Header';
import ConferenciaModal from './Modals/ConferenciaModal';
import DetalhesModal from './Modals/DetalhesModal';
import Table from './Modals/Table';
import styles from './styles';

const Conferencia = () => {
  const titulo = 'Conferência';
  const navigate = useNavigate();
  const { hash } = useParams();
  const docs = decryptURL(hash);
  const scrollRef = useRef(null);
  const { openDialog, closeDialog } = useDialog();
  const [selected, setSelected] = useState(null);
  const [isFading, setIsFading] = useState(false);
  const [documentos, setDocumentos] = useState([]);
  const {
    getConferencia,
    getLoading,
    postConferencia,
    postLoading,
    deleteConferencia,
    deleteLoading,
    postLote,
  } = useContext(ArmazenagemContext);
  const loading = getLoading || postLoading || deleteLoading;
  const Itenspedido = selected?.Itens?.map(
    (m) => Boolean(m?.Itenspedido) && m?.Itenspedido
  )?.filter(Boolean);

  const loadData = () => {
    const ids = (documentos?.length > 0 ? documentos : docs)?.map((d) => d?.id);
    const params = { ids };
    const cb = (data) => {
      setDocumentos(data);
      if (Boolean(data?.length)) {
        const newSelected = data?.find((f) => f?.id === selected?.id);
        setSelected(newSelected || data?.[0]);
      }
      closeDialog();
    };
    getConferencia({ params, cb });
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleChipClick = (documento, index) => {
    setIsFading(true);
    setTimeout(() => {
      const container = scrollRef.current;
      const chip = container.children[index];

      const chipLeft = chip.offsetLeft;
      const chipWidth = chip.offsetWidth;
      const containerWidth = container.offsetWidth;

      const scrollPosition = chipLeft - containerWidth / 2 + chipWidth / 2;

      container.scrollTo({ left: scrollPosition, behavior: 'smooth' });

      setSelected(documento);
      setIsFading(false);
    }, 200);
  };

  const handleDelete = (documento) => {
    setDocumentos((prev) => {
      const newArr = prev?.filter((f) => f?.id !== documento?.id);
      if (!newArr?.some((s) => s?.id === selected?.id)) {
        setSelected(newArr?.[0] || null);
        scrollRef.current.scrollTo({ left: 0, behavior: 'smooth' });
      }
      return newArr;
    });
  };

  const handleSubmit = (data) => postConferencia({ data, cb: loadData });

  const onSubmit = (row) => {
    const checkModal = row?.rastreavel === 'SIM' || row?.variavel === 'SIM';

    if (row?.conferido) {
      openDialog(<DetalhesModal item={row} />, row?.descricao);
    } else {
      if (checkModal) {
        openDialog(
          <ConferenciaModal handleSubmit={handleSubmit} item={row} />,
          row?.descricao
        );
      } else {
        handleSubmit([
          { documento_item_id: row?.id, quantidade: row?.quantidade },
        ]);
      }
    }
  };

  const onSubmitAll = () => {
    const itens = [];
    const variaveis = [];
    selected?.Itens?.map((item) => {
      const quantidade_conferida =
        item?.Conferidos?.reduce(
          (acc, item) => acc + item?.quantidade || 0,
          0
        ) || 0;

      if (
        quantidade_conferida != item?.quantidade &&
        item?.variavel !== 'SIM' &&
        item?.rastreavel !== 'SIM'
      ) {
        itens?.push({
          documento_item_id: item?.id,
          quantidade: (item?.quantidade || 0) - quantidade_conferida,
        });
      } else {
        if ((item?.quantidade || 0) - quantidade_conferida > 0) {
          variaveis?.push(item);
        }
      }
    });

    handleSubmit(itens);

    if (variaveis?.length) {
      toastWarning(
        <div>
          Atenção!
          <br />
          Produtos não conferidos por falta de variação/rastreabilidade.
          <br />
          <br />
          {variaveis?.map((m, index) => (
            <div key={index}>• {m?.descricao}</div>
          ))}
        </div>
      );
    }
  };

  const onDelete = (row) => {
    const params = { documento_item_id: row?.id };
    deleteConferencia({ params, cb: loadData });
  };

  const handleSubmitLote = () => {
    let conferencia = true;
    documentos?.map((d) => {
      (d?.Itens || [])?.map((di) => {
        const qt_item = di?.quantidade;
        const qt_conf =
          (di?.Conferidos || [])?.reduce(
            (acc, item) => acc + item?.quantidade || 0,
            0
          ) || 0;
        if (qt_item != qt_conf) {
          conferencia = false;
        }
      });
    });
    if (conferencia) {
      const data = { ids: documentos?.map((d) => d?.id) };
      postLote({ data, cb: () => navigate(-1) });
    } else {
      toastWarning(
        'Você deve conferir todos os itens dos documentos para gerar o lote'
      );
    }
  };

  const showDoneAll = () => {
    return selected?.Itens?.map((item) => {
      const quantidade_conferida =
        item?.Conferidos?.reduce(
          (acc, item) => acc + item?.quantidade || 0,
          0
        ) || 0;

      return (
        quantidade_conferida != item?.quantidade &&
        item?.variavel !== 'SIM' &&
        item?.rastreavel !== 'SIM'
      );
    })?.some((s) => Boolean(s));
  };

  const showPrint = () => {
    return selected?.Itens?.map((item) => {
      const quantidade_conferida =
        item?.Conferidos?.reduce(
          (acc, item) => acc + item?.quantidade || 0,
          0
        ) || 0;

      return quantidade_conferida == item?.quantidade;
    })?.every((s) => Boolean(s));
  };

  const gridProps = { loading, onDelete, onSubmit };

  return (
    <Container>
      <Header titulo={titulo} />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box ref={scrollRef} sx={styles?.chipContainer}>
            {documentos?.map((d, index) => (
              <Chip
                clickable
                key={index}
                color="primary"
                sx={styles?.chip}
                label={`${d?.natureza || ''}${d?.documento ? ' • Nº ' : ''}${
                  d?.documento || ''
                }`}
                onClick={() => handleChipClick(d, index)}
                variant={selected?.id === d?.id ? 'contained' : 'outlined'}
                icon={<Description fontSize="small" />}
                onDelete={() => handleDelete(d)}
              />
            ))}
          </Box>
        </Grid>
        <Grid
          item
          xs={12}
          display="flex"
          flexDirection="row"
          justifyContent="flex-end"
          gap={2}
          mt={2}
        >
          {showDoneAll() && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<DoneAll />}
              onClick={onSubmitAll}
            >
              Conferir Todos
            </Button>
          )}
          {showPrint() && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<Print />}
              onClick={() =>
                openDialog(
                  <EtiquetaModal itens={selected?.Itens} armazenagem />,
                  'Imprimir Etiquetas'
                )
              }
            >
              Imprimir Etiquetas
            </Button>
          )}
        </Grid>
        {Boolean(selected) && (
          <Grid item xs={12} sx={isFading ? styles?.fadeOut : styles?.fadeIn}>
            <Grid container spacing={2}>
              {Boolean(selected?.Pedido) && (
                <Grid item xs={12} md={6}>
                  <Box style={styles?.card}>
                    <Typography variant="body1" fontWeight="bold">
                      {selected?.Pedido?.natureza}
                      {selected?.Pedido?.documento
                        ? ` • Nº ${selected?.Pedido?.documento}`
                        : ''}
                    </Typography>
                    <Typography variant="caption">
                      {selected?.Pedido?.fornecedor}
                    </Typography>
                    <Table {...gridProps} rows={Itenspedido || []} />
                  </Box>
                </Grid>
              )}
              <Grid item xs={12} md={Boolean(selected?.Pedido) ? 6 : 12}>
                <Box style={styles?.card}>
                  <Typography variant="body1" fontWeight="bold">
                    {selected?.natureza}
                    {selected?.documento ? ` • Nº ${selected?.documento}` : ''}
                  </Typography>
                  <Typography variant="caption">
                    {selected?.fornecedor}
                  </Typography>
                  <Table {...gridProps} rows={selected?.Itens || []} isNota />
                </Box>
              </Grid>
            </Grid>
          </Grid>
        )}
        <Grid item xs={12} textAlign="center">
          <Button
            loading={loading}
            variant="contained"
            onClick={handleSubmitLote}
          >
            Gerar Lote
          </Button>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Conferencia;
